import { z } from 'zod';
import { IsoDateTime } from './common.js';

/**
 * Fournisseurs connectés (CDC §3.2, table `connections`). Pas de Stripe : les abonnements sont lus
 * dans le back Evolusite (CDC §3.2 ter).
 */
export const ProviderName = z.enum([
  'axonaut',
  'google',
  'anthropic',
  'evolusite',
  'google_places',
  // Lot BB : conversations Crisp du support (lecture, message opérateur après validation).
  'crisp',
]);
export type ProviderName = z.infer<typeof ProviderName>;
export const PROVIDER_NAMES = ProviderName.options;

export const ConnectionMode = z.enum(['fake', 'real']);
export type ConnectionMode = z.infer<typeof ConnectionMode>;

export const ConnectionStatus = z.enum(['NOT_CONFIGURED', 'OK', 'ERROR']);
export type ConnectionStatus = z.infer<typeof ConnectionStatus>;

/** Modèles proposés pour l'IA (CDC §0). */
export const AiModel = z.enum(['claude-opus-5-5', 'claude-sonnet-5-5']);
export type AiModel = z.infer<typeof AiModel>;
export const DEFAULT_AI_MODEL: AiModel = 'claude-opus-5-5';

/**
 * Champs secrets de chaque fournisseur. Ils sont chiffrés ensemble (AES-256-GCM) dans
 * `connections.secret_enc` et ne sont jamais renvoyés : seulement `hasSecret` et `secretHint`.
 * Le premier champ de chaque liste est le secret « principal » (celui de `hasSecret`/`secretHint`).
 */
export const PROVIDER_SECRET_FIELDS = {
  axonaut: ['apiKey'],
  google: [],
  anthropic: ['apiKey'],
  evolusite: ['apiKey'],
  // Lot AN : clé Google Places API (New), qui sert aussi à PageSpeed Insights (fiches R1, lecture seule).
  google_places: ['apiKey'],
  // Lot BB : jeton de site Crisp (identifiant + clé, authentification Basic), chiffré, jamais journalisé.
  crisp: ['key', 'identifier'],
} as const satisfies Record<ProviderName, readonly string[]>;
export type ProviderSecretField<P extends ProviderName> = (typeof PROVIDER_SECRET_FIELDS)[P][number];

/** Réglages non secrets de chaque fournisseur (`connections.config`). */
export const ConnectionConfigSchemas = {
  axonaut: z.object({}).strict(),
  google: z
    .object({
      /**
       * Ancien agenda partagé de l'agence : accepté pour les configurations existantes mais IGNORÉ depuis le
       * lot H (chaque membre a son propre agenda, lu avec son compte Google).
       */
      calendarId: z.string().trim().min(1).max(256).optional(),
      /** Espace Google Chat du groupe Evolusite, ex. `spaces/AAAAxxxx`. */
      groupSpace: z
        .string()
        .trim()
        .regex(/^spaces\/[A-Za-z0-9_-]+$/, 'Espace attendu au format spaces/…')
        .optional(),
      /** Choix Google Chat proposé en premier dans l'écran de validation (prioritaire sur `settings.chat`). */
      chatDefault: z.enum(['dm', 'group']).optional(),
      /**
       * Boîte partagée (lot L) : réglée seulement par `PUT /api/admin/google/shared-mailboxes` (le `PUT`
       * générique de la connexion la refuse). La clé JSON du compte de service est un secret
       * (`serviceAccountKey`, chiffré avec les autres secrets de la connexion, jamais renvoyé) ; ici, seulement
       * l'identité du compte de service, les adresses et le résultat du dernier test de chacune.
       */
      sharedMailbox: z
        .object({
          addresses: z.array(z.string()).max(5),
          serviceAccount: z
            .object({
              clientEmail: z.string(),
              clientId: z.string(),
              projectId: z.string().nullable(),
            })
            .strict()
            .optional(),
          tests: z
            .record(z.string(), z.object({ ok: z.boolean(), at: z.string(), message: z.string() }).strict())
            .optional(),
        })
        .strict()
        .optional(),
    })
    .strict(),
  anthropic: z
    .object({
      model: AiModel.optional(),
      /** Plafond mensuel de dépense, en centimes de dollar (Anthropic facture en USD ; CDC §0 : réglable). */
      monthlyCapUsdCents: z.int().min(0).max(100_000_00).optional(),
      /** Ancien nom (avant le lot C1), accepté en lecture ; la migration 202610130501 le renomme. */
      monthlyCapCents: z.int().min(0).max(100_000_00).optional(),
    })
    .strict(),
  evolusite: z
    .object({
      /** URL du back Evolusite (CDC §3.9), ex. `https://api.evolusite.fr`. */
      apiUrl: z.url({ protocol: /^https?$/ }).optional(),
      /**
       * Adresse d'un site dans l'admin Evolusite, `{idSite}` remplacé par l'identifiant du site (lot Z) :
       * lien « Admin Evolusite » des actions et du panneau d'un projet.
       */
      adminSiteUrlTemplate: z
        .string()
        .trim()
        .max(500)
        .regex(/^https?:\/\/\S*\{idSite\}\S*$/, 'Adresse http(s) contenant {idSite} attendue')
        .optional(),
    })
    .strict(),
  // Lot AN : rien à régler en dehors de la clé.
  google_places: z.object({}).strict(),
  /**
   * Lot BB : site Crisp lu (`websiteId`, obligatoire pour lire) et niveau du jeton (`X-Crisp-Tier`, `website` par
   * défaut ; `plugin` quand le jeton de site sera remplacé par un jeton de plugin).
   */
  crisp: z
    .object({
      websiteId: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9-]{1,64}$/, 'Identifiant de site Crisp attendu (lettres, chiffres, tirets)')
        .optional(),
      tier: z.enum(['website', 'plugin']).optional(),
    })
    .strict(),
} as const satisfies Record<ProviderName, z.ZodType>;
export type ConnectionConfig<P extends ProviderName> = z.infer<(typeof ConnectionConfigSchemas)[P]>;

export const SecretState = z.object({
  hasSecret: z.boolean(),
  /** Les 4 derniers caractères du secret, ou null. */
  secretHint: z.string().nullable(),
});
export type SecretState = z.infer<typeof SecretState>;

/** `GET /api/admin/connections/:provider` : jamais de secret en clair. */
export const Connection = z.object({
  provider: ProviderName,
  /** Mode choisi dans les Réglages. */
  mode: ConnectionMode,
  /** Mode réellement appliqué (`fake` si FORCE_FAKE est actif). */
  effectiveMode: ConnectionMode,
  forcedFake: z.boolean(),
  status: ConnectionStatus,
  hasSecret: z.boolean(),
  secretHint: z.string().nullable(),
  /** État de chaque champ secret (Crisp : clé et identifiant). */
  secrets: z.record(z.string(), SecretState),
  config: z.record(z.string(), z.unknown()),
  lastTestAt: IsoDateTime.nullable(),
  lastSyncAt: IsoDateTime.nullable(),
  lastError: z.string().nullable(),
  /** Une synchro est en file ou en cours (tâche pg-boss `sync:<fournisseur>`) ; rendu par `GET …/:provider`. */
  syncRunning: z.boolean().optional(),
});
export type Connection = z.infer<typeof Connection>;

export const ProviderParams = z.object({ provider: ProviderName });

/**
 * `PUT /api/admin/connections/:provider`. Pour un secret : une chaîne le remplace, `null` l'efface,
 * l'absence le laisse inchangé. `config` est fusionné avec la configuration existante et validé par
 * le schéma du fournisseur (une clé à `null` est retirée).
 */
export const UpdateConnectionBody = z
  .object({
    mode: ConnectionMode.optional(),
    secrets: z.record(z.string(), z.string().trim().min(1).max(500).nullable()).optional(),
    config: z.record(z.string(), z.unknown()).optional(),
  })
  .refine((body) => body.mode !== undefined || body.secrets !== undefined || body.config !== undefined, {
    message: 'Aucune modification demandée',
  });
export type UpdateConnectionBody = z.input<typeof UpdateConnectionBody>;

/** `POST /api/admin/connections/:provider/test` */
export const ConnectionTestResult = z.object({
  ok: z.boolean(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
  connection: Connection,
});
export type ConnectionTestResult = z.infer<typeof ConnectionTestResult>;

/** `POST /api/admin/connections/:provider/sync` (202) : la synchro part dans la file pg-boss. */
export const ConnectionSyncQueued = z.object({
  queued: z.literal(true),
  queue: z.string(),
  jobId: z.string(),
});
export type ConnectionSyncQueued = z.infer<typeof ConnectionSyncQueued>;
